/**
 * Aviso de la sección de plataforma cuando el backend niega el acceso de
 * plataforma (OT-0073 §4.1, capa 5):
 *
 *   - `PLATFORM_MFA_REQUIRED`: el token no acredita segundo factor (`amr`);
 *     se explica que hay que volver a entrar con el código por SMS.
 *   - `PLATFORM_OPERATOR_REQUIRED`: el usuario no es del tenant operador.
 *
 * `PlatformAccessGate` envuelve cada pantalla de plataforma: pregunta una vez
 * al backend (`GET /platform/tenants/defaults`, que pasa por el mismo guard) y,
 * si niega por una de esas dos razones, muestra el aviso en lugar de la
 * pantalla. Cualquier otro error lo sigue mostrando cada vista como siempre.
 */

import type { ReactNode } from 'react'
import { Loader2, ShieldAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { platformAccessDenial, type PlatformAccessDenial } from '../../api/client'
import { useSignOut } from '../../auth/useSignOut'
import { usePlatformTenantDefaults } from '../../hooks/usePlatform'
import { ScrollPage } from '../shell/ScrollPage'

export function PlatformAccessError({ denial }: { denial: PlatformAccessDenial }) {
  const { t } = useTranslation('platform')
  const signOut = useSignOut()

  return (
    <section
      role="alert"
      className="max-w-lg rounded-md border border-review/40 bg-review-soft p-6"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-review" aria-hidden />
        <div>
          <h2 className="font-display text-lg text-ink">
            {t(`clients.access_error.${denial}_title`)}
          </h2>
          <p className="mt-1 font-body text-sm text-ink-muted">
            {t(`clients.access_error.${denial}_body`)}
          </p>
          {denial === 'mfa' && (
            <button
              type="button"
              onClick={signOut}
              className="mt-4 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-canvas hover:bg-brand/90"
            >
              {t('clients.access_error.sign_out')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

export function PlatformAccessGate({ children }: { children: ReactNode }) {
  const probe = usePlatformTenantDefaults()
  if (probe.isPending) {
    return (
      <ScrollPage centered>
        <Loader2 className="h-5 w-5 animate-spin text-ink-subtle" aria-hidden />
      </ScrollPage>
    )
  }
  const denial = platformAccessDenial(probe.error)
  if (denial) {
    return (
      <ScrollPage centered>
        <PlatformAccessError denial={denial} />
      </ScrollPage>
    )
  }
  return <>{children}</>
}
